import { withTriggerCombobox } from '@platejs/combobox'
import { createPlatePlugin } from 'platejs/react'
import type { AnyPlatePlugin } from 'platejs/react'

import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

import type { AutocompletePluginOptions } from '../autocomplete'
import { replaceTextAfterTriggerWithMention } from '../autocomplete/transforms/replaceTextAfterTriggerWithMention'
import { withInsertTriggerBeforeText } from '../autocomplete/withInsertTriggerBeforeText'
import { getNewSuggestionsOnMentionUpdate } from './api/getNewSuggestionsOnMentionUpdate'
import { areMentionsSupported } from './commands/areMentionsSupported'
import { findAndInsertFacebookMentions } from './commands/findAndInsertFacebookMentions'
import { FacebookMentionInputElement } from './nodes/FacebookMentionInputElement'
import { hasOnlyFacebookGroupsSelected } from './queries/hasOnlyFacebookGroupsSelected'
import { FacebookMentionInputElementUI } from './ui/FacebookMentionInputElementUI'

// Not returned directly from the arrow function: an explicit return type on
// the expression itself back-propagates into createPlatePlugin's own
// generic inference and corrupts it (options/node end up typed against the
// annotation's defaults instead of the literal passed in).
export const createFacebookMentionPlugin = (overrides?: {
  options?: Partial<AutocompletePluginOptions<FacebookMentionInputElement>>
}): AnyPlatePlugin => {
  const createdPlugin = createPlatePlugin({
    key: FacebookMentionInputElement.type,
    node: {
      isElement: true,
      isInline: true,
      isVoid: true,
      component: FacebookMentionInputElementUI,
    },
    options: {
      trigger: FacebookMentionInputElement.trigger,
      triggerPreviousCharPattern: /^\s?$/,
      // withTriggerCombobox only allows the trigger when triggerQuery
      // returns true — a Group-only channel selection blocks it entirely.
      triggerQuery: (editor: BufferEditor) =>
        !hasOnlyFacebookGroupsSelected(editor),
      createComboboxInput: () => FacebookMentionInputElement.new(),
      textToNodes: findAndInsertFacebookMentions,
      fetchNewSuggestions: getNewSuggestionsOnMentionUpdate,
      fetchDebounceMs: 300,
      onInsertTriggerBeforeText: replaceTextAfterTriggerWithMention,
      areMentionsSupported,
      ...overrides?.options,
    } as AutocompletePluginOptions<FacebookMentionInputElement>,
    extendEditor: ({ editor: rawEditor, plugin }) => {
      const editor = rawEditor as unknown as BufferEditor
      const options =
        plugin.options as AutocompletePluginOptions<FacebookMentionInputElement>
      // Exposed for AppStore, which checks this outside any plugin context
      // to decide whether to surface the "mentions unsupported" toast.
      editor.areMentionsSupported = options.areMentionsSupported
      return editor
    },
  })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .overrideEditor(withTriggerCombobox as any)
    .overrideEditor(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      withInsertTriggerBeforeText(FacebookMentionInputElement) as any,
    )
  return createdPlugin
}
